import { CheckCircle2, Eye, FileCheck, FileText, FileX, Image as ImageIcon, PenTool } from "lucide-react";
import { useState } from "react";
import type { Mandate } from "@/types/mandate";

interface Props {
  mandate: Mandate;
}

export default function CustomerDocuments({ mandate }: Props) {
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const [previewTitle, setPreviewTitle] = useState<string>("");

  const hasPhoto = Boolean(mandate.customerPhoto);
  const hasSig = Boolean(mandate.signature);
  const hasIdProof = Boolean(mandate.idProofType || mandate.idProofFileName);
  const hasAddressProof = Boolean(mandate.addressProofType || mandate.addressProofFileName);
  const extraDocs = Array.isArray(mandate.documents) ? mandate.documents : [];
  const hasExtraDocs = extraDocs.length > 0;

  const hasAnyDoc = hasPhoto || hasSig || hasIdProof || hasAddressProof || hasExtraDocs;

  if (!hasAnyDoc) {
    return (
      <div className="rounded-[10px] border border-line bg-white p-5 shadow-card">
        <h3 className="text-lg font-semibold text-ink">Customer Documents</h3>
        <div className="mt-4 flex flex-col items-center gap-2 rounded-lg border border-dashed border-slate-300 py-8 text-center">
          <FileX className="h-6 w-6 text-ink-muted" />
          <p className="text-sm text-ink-secondary">No documents are on file for this mandate.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-[10px] border border-line bg-white p-5 shadow-card">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-ink">Customer Documents</h3>
        <span className="inline-flex items-center gap-1 rounded-full bg-success-bg px-2.5 py-0.5 text-xs font-medium text-success">
          <CheckCircle2 className="h-3.5 w-3.5" />
          Verified
        </span>
      </div>

      <div className="mt-4 flex flex-col gap-3">
        {/* Customer Photograph */}
        {hasPhoto && (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-line bg-[#F8FAFC] p-3 transition-colors hover:bg-slate-100/60">
            <div className="flex items-center gap-3 min-w-0">
              <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-md border border-slate-200 bg-white">
                <img
                  src={mandate.customerPhoto!}
                  alt="Customer"
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-ink">Customer Photograph</p>
                <p className="truncate text-xs text-ink-secondary">Face capture at counter</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                setPreviewSrc(mandate.customerPhoto!);
                setPreviewTitle("Customer Photograph");
              }}
              className="inline-flex shrink-0 items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-ink hover:bg-slate-50 cursor-pointer"
            >
              <Eye className="h-3.5 w-3.5 text-ink-secondary" />
              View
            </button>
          </div>
        )}

        {/* Customer Signature */}
        {hasSig && (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-line bg-[#F8FAFC] p-3 transition-colors hover:bg-slate-100/60">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex h-12 w-16 shrink-0 items-center justify-center overflow-hidden rounded-md border border-slate-200 bg-white p-1">
                <img
                  src={mandate.signature!}
                  alt="Signature"
                  className="max-h-full max-w-full object-contain"
                />
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-ink">Customer Signature</p>
                <p className="truncate text-xs text-ink-secondary">Digital counter signature</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                setPreviewSrc(mandate.signature!);
                setPreviewTitle("Customer Signature");
              }}
              className="inline-flex shrink-0 items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-ink hover:bg-slate-50 cursor-pointer"
            >
              <Eye className="h-3.5 w-3.5 text-ink-secondary" />
              View
            </button>
          </div>
        )}

        {/* ID Proof */}
        {hasIdProof && (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-line bg-[#F8FAFC] p-3 transition-colors hover:bg-slate-100/60">
            <div className="flex items-center gap-3 min-w-0">
              {mandate.idProofDocument && !mandate.idProofDocument.startsWith("data:application/pdf") ? (
                <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-md border border-slate-200 bg-white">
                  <img
                    src={mandate.idProofDocument}
                    alt={mandate.idProofType || "ID Proof"}
                    className="h-full w-full object-cover"
                  />
                </div>
              ) : (
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-500">
                  <FileCheck className="h-5 w-5" />
                </span>
              )}
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-ink">
                  {mandate.idProofType || "ID Proof"}
                </p>
                <p className="truncate text-xs text-ink-secondary">
                  {mandate.idProofFileName || "Attached Document"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                Verified
              </span>
              {mandate.idProofDocument && (
                <button
                  type="button"
                  onClick={() => {
                    setPreviewSrc(mandate.idProofDocument!);
                    setPreviewTitle(mandate.idProofType || "ID Proof");
                  }}
                  className="inline-flex shrink-0 items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-ink hover:bg-slate-50 cursor-pointer"
                >
                  <Eye className="h-3.5 w-3.5 text-ink-secondary" />
                  View
                </button>
              )}
            </div>
          </div>
        )}

        {/* Address Proof */}
        {hasAddressProof && (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-line bg-[#F8FAFC] p-3 transition-colors hover:bg-slate-100/60">
            <div className="flex items-center gap-3 min-w-0">
              {mandate.addressProofDocument && !mandate.addressProofDocument.startsWith("data:application/pdf") ? (
                <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-md border border-slate-200 bg-white">
                  <img
                    src={mandate.addressProofDocument}
                    alt={mandate.addressProofType || "Address Proof"}
                    className="h-full w-full object-cover"
                  />
                </div>
              ) : (
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-500">
                  <FileText className="h-5 w-5" />
                </span>
              )}
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-ink">
                  {mandate.addressProofType || "Address Proof"}
                </p>
                <p className="truncate text-xs text-ink-secondary">
                  {mandate.addressProofFileName || "Attached Document"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
                Verified
              </span>
              {mandate.addressProofDocument && (
                <button
                  type="button"
                  onClick={() => {
                    setPreviewSrc(mandate.addressProofDocument!);
                    setPreviewTitle(mandate.addressProofType || "Address Proof");
                  }}
                  className="inline-flex shrink-0 items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-ink hover:bg-slate-50 cursor-pointer"
                >
                  <Eye className="h-3.5 w-3.5 text-ink-secondary" />
                  View
                </button>
              )}
            </div>
          </div>
        )}

        {/* Extra documents from array */}
        {extraDocs.map((doc: any, idx: number) => (
          <div
            key={idx}
            className="flex items-center justify-between gap-3 rounded-lg border border-line bg-[#F8FAFC] p-3 transition-colors hover:bg-slate-100/60"
          >
            <div className="flex items-center gap-3 min-w-0">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-500">
                <FileText className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-ink">
                  {doc.name || doc.type || `Document #${idx + 1}`}
                </p>
                <p className="truncate text-xs text-ink-secondary">{doc.fileName || doc.status || "Attached"}</p>
              </div>
            </div>
            <span className="shrink-0 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
              {doc.status || "Verified"}
            </span>
          </div>
        ))}
      </div>

      {/* Image Preview Modal */}
      {previewSrc && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
          onClick={() => setPreviewSrc(null)}
        >
          <div
            className="relative max-h-[85vh] max-w-lg overflow-hidden rounded-xl border border-line bg-white p-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between">
              <h4 className="text-base font-semibold text-ink">{previewTitle}</h4>
              <button
                type="button"
                onClick={() => setPreviewSrc(null)}
                className="rounded-md p-1 text-ink-muted hover:bg-slate-100 hover:text-ink cursor-pointer"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="flex items-center justify-center overflow-auto rounded-lg bg-slate-50 p-2">
              <img
                src={previewSrc}
                alt={previewTitle}
                className="max-h-[60vh] max-w-full rounded object-contain shadow-sm"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
